/**
 * Registro dos pontos de ajuda contextual.
 *
 * As telas nao escrevem `slug` e `#ancora` a mao: pedem um ponto pelo nome. Se
 * um artigo for renomeado, o erro aparece em tempo de compilacao aqui, em um
 * lugar, em vez de virar link quebrado espalhado pela interface.
 *
 * Cada ponto responde a UMA pergunta concreta que a tela provoca.
 */

import { HELP_BY_SLUG } from './content';

export interface HelpTarget {
  slug: string;
  /** Âncora da seção. `null` abre o artigo do começo. */
  section: string | null;
  /** Texto do link, quando a tela não informa outro. */
  label: string;
}

export const HELP_POINTS = {
  'contatos.sem-empresa': {
    slug: 'contatos-sem-empresa',
    section: 'quando',
    label: 'Quando cadastrar sem empresa?',
  },
  'contatos.associar-empresa': {
    slug: 'contatos-sem-empresa',
    section: 'associar',
    label: 'Como associar depois?',
  },
  'contatos.principal': {
    slug: 'contatos',
    section: 'principal',
    label: 'O que é contato principal?',
  },
  'contatos.consentimento': {
    slug: 'contatos',
    section: 'consentimento',
    label: 'Por que registrar consentimento?',
  },
  'contatos.papel': { slug: 'contatos', section: 'papel', label: 'Como escolher o papel?' },
  'duplicidade.aviso': {
    slug: 'duplicidades',
    section: 'como-funciona',
    label: 'Por que isto é aviso e não bloqueio?',
  },
  'empresas.cadastro': { slug: 'empresas', section: 'cadastro', label: 'O que cada campo significa?' },
  'empresas.grupo': {
    slug: 'empresas',
    section: 'grupo-economico',
    label: 'Quando usar grupo econômico?',
  },
  'oportunidades.tipos': {
    slug: 'oportunidades',
    section: 'tipos',
    label: 'Qual tipo de oferta escolher?',
  },
  'oportunidades.valores': {
    slug: 'oportunidades',
    section: 'valores',
    label: 'Valor único ou mensal?',
  },
  'pipeline.exigencias': {
    slug: 'pipeline',
    section: 'exigencias',
    label: 'Por que a etapa recusa o avanço?',
  },
  'pipeline.fechamento': {
    slug: 'pipeline',
    section: 'fechamento',
    label: 'Como registrar ganha ou perdida?',
  },
  'atividades.proximo-passo': {
    slug: 'atividades',
    section: 'proximo-passo',
    label: 'Por que agendar o próximo passo?',
  },
  'parceiros.isolamento': {
    slug: 'parceiros',
    section: 'isolamento',
    label: 'O que um parceiro consegue ver?',
  },
  'reservas.ciclo': { slug: 'reservas-de-conta', section: 'ciclo', label: 'Como funciona o prazo?' },
  'reservas.conflito': {
    slug: 'reservas-de-conta',
    section: 'conflito',
    label: 'O que é conflito de atribuição?',
  },
  'permissoes.escopo': {
    slug: 'permissoes',
    section: 'como-decide',
    label: 'Como o acesso é decidido?',
  },
  'permissoes.nao-encontrado': {
    slug: 'permissoes',
    section: 'nao-encontrado',
    label: 'Por que aparece "não encontrado"?',
  },
  'auditoria.o-que-registra': {
    slug: 'auditoria',
    section: 'o-que-registra',
    label: 'O que é registrado?',
  },
  'usuarios.senha': { slug: 'usuarios', section: 'senha', label: 'Como definir a senha de alguém?' },
  'demo.criterio': {
    slug: 'dados-de-demonstracao',
    section: 'como-identifica',
    label: 'Como o sistema distingue exemplo de dado real?',
  },
  'demo.limpar': { slug: 'dados-de-demonstracao', section: 'limpar', label: 'O que a limpeza remove?' },
  'seguranca.segundo-fator': {
    slug: 'segundo-fator',
    section: 'cadastrar',
    label: 'Como cadastrar o aplicativo autenticador?',
  },
  'seguranca.perdi-celular': {
    slug: 'segundo-fator',
    section: 'perdi',
    label: 'E se eu perder o celular?',
  },
  'regras.vigencia': {
    slug: 'regras-comerciais',
    section: 'vigencia',
    label: 'O que acontece com as reservas já aprovadas?',
  },
  'primeiros-passos.roteiro': {
    slug: 'primeiro-dia',
    section: 'roteiro',
    label: 'Ver o roteiro do primeiro dia',
  },
} as const satisfies Record<string, HelpTarget>;

export type HelpPointKey = keyof typeof HELP_POINTS;

/** Caminho pronto para `<Link to=…>`. */
export function helpPath(key: HelpPointKey): string {
  // Anotação explícita: sem ela o tipo literal de `section` torna a comparação
  // com `null` impossível e o TypeScript reduz o outro ramo a `never`.
  const point: HelpTarget = HELP_POINTS[key];
  return point.section === null ? `/ajuda/${point.slug}` : `/ajuda/${point.slug}#${point.section}`;
}

/**
 * Confere, em desenvolvimento, que todo ponto aponta para artigo e seção que
 * existem. Um link de ajuda quebrado é pior do que ausência de link: ele promete
 * explicação e entrega tela vazia.
 */
export function validateHelpPoints(): string[] {
  const problems: string[] = [];
  for (const [key, point] of Object.entries(HELP_POINTS) as [string, HelpTarget][]) {
    const article = HELP_BY_SLUG.get(point.slug);
    if (article === undefined) {
      problems.push(`${key}: artigo "${point.slug}" não existe`);
      continue;
    }
    if (point.section !== null && !article.sections.some((s) => s.id === point.section)) {
      problems.push(`${key}: seção "${point.section}" não existe em "${point.slug}"`);
    }
  }
  return problems;
}
